import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { styled } from "@mui/material/styles";
import { DataGrid, GridToolbarContainer, GridToolbarFilterButton, GridToolbarColumnsButton, GridToolbarDensitySelector } from "@mui/x-data-grid";
import ExportIcon from "../components/ExportIcon";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS_MUI } from "../config/maintenanceConfig";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/* ─── Styling constants ─── */
const primaryColor = "#005EB8";
const darkGrey = "#D3D3D3";

/* ─── Styled DataGrid (same look as other reports) ─── */
const StyledDataGrid = styled(DataGrid)(() => ({
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold !important",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    backgroundColor: primaryColor,
    color: "#FFFFFF !important",
    fontSize: "0.95rem !important",
    textTransform: "none",
    borderBottom: `2px solid ${primaryColor}`,
  },
  "& .MuiDataGrid-sortIcon": {
    color: "#FFFFFF !important",
    opacity: "1 !important",
  },
  "& .MuiDataGrid-menuIconButton": {
    color: "#FFFFFF !important",
  },
  "& .MuiDataGrid-filterIcon": {
    color: "#FFFFFF !important",
  },
  "& .MuiDataGrid-cell": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: `1px solid ${darkGrey}`,
  },
  "& .MuiDataGrid-columnHeaders": {
    whiteSpace: "normal",
  },
}));

/* ─── Date/time formatter ─── */
const formatDateTime = (value) => {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return value;
  }
};

/* ─── Static column definitions ─── */
const COLUMNS_CONFIG = [
  { field: "so_id",                  headerName: "SO ID",                    width: 150 },
  { field: "log_id",                 headerName: "Log ID",                   width: 120 },
  { field: "released_employee_id",   headerName: "Released Employee ID",     width: 190 },
  { field: "released_employee_name", headerName: "Released Employee Name",   width: 220 },
  { field: "source",                 headerName: "Source",                   width: 160 },
  { field: "proposed_by",            headerName: "Proposed By",              width: 180 },
  {
    field: "proposed_on",
    headerName: "Proposed On",
    width: 200,
    valueFormatter: (params) => formatDateTime(params),
  },
  {
    field: "auto_release_time",
    headerName: "Auto Release Time",
    width: 200,
    valueFormatter: (params) => formatDateTime(params),
  },
];

/* ─── Custom toolbar (filter + column + density only — no built-in export) ─── */
function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );
}

/* ─── Main component ─── */
export default function AutoReleaseHistory() {
  const baseURI = process.env.REACT_APP_API_BASE_URL ?? "";
  const gridRef = useRef(null);

  const [loading, setLoading]               = useState(true);
  const [allRows, setAllRows]               = useState([]);
  const [rows, setRows]                     = useState([]);
  const [distinctSoIds, setDistinctSoIds]   = useState([]);
  const [distinctProposedBy, setDistinctProposedBy] = useState([]);
  const [filters, setFilters]               = useState({ so_id: [], proposed_by: [] });

  /* ─── Fetch data on mount ─── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${baseURI}/autoReleaseLog`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const rowsData = Array.isArray(data) ? data : [data];
        const mapped = rowsData.map((r, idx) => ({
          id: r.log_id ?? idx,
          ...r,
        }));

        setAllRows(mapped);
        setRows(mapped);

        // Compute distinct values for top-level filters
        setDistinctSoIds(
          [...new Set(mapped.map((r) => r.so_id).filter(Boolean))].sort()
        );
        setDistinctProposedBy(
          [...new Set(mapped.map((r) => r.proposed_by).filter(Boolean))].sort()
        );
      } catch (err) {
        console.error("AutoReleaseHistory fetch error:", err);
        setAllRows([]);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Apply top-level filters ─── */
  useEffect(() => {
    let filtered = [...allRows];

    if (filters.so_id?.length) {
      filtered = filtered.filter((r) =>
        filters.so_id.includes(String(r.so_id))
      );
    }
    if (filters.proposed_by?.length) {
      filtered = filtered.filter((r) =>
        filters.proposed_by.includes(String(r.proposed_by))
      );
    }

    setRows(filtered);
  }, [filters, allRows]);

  /* ─── Clear all filters ─── */
  const handleClearFilters = () => {
    setFilters({ so_id: [], proposed_by: [] });
  };

  /* ─── Column definitions with sortable + filterable ─── */
  const columns = useMemo(
    () =>
      COLUMNS_CONFIG.map((col) => ({
        ...col,
        sortable: true,
        filterable: true,
      })),
    []
  );

  return (
    <Box p={1}>
      {/* ── Full-screen loader ── */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit" sx={{ textAlign: "center" }}>
            Loading Auto Release History...
          </Typography>
        </Box>
      </Backdrop>

      {/* ── Header: title + top-level filters + action buttons ── */}
      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={1} alignItems="center">
          {/* Title */}
          <Grid item xs={12} md={7}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: primaryColor,
                mb: 1,
                textAlign: "left",
              }}
            >
              Auto Release History
            </Typography>
          </Grid>

          {/* Action buttons */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ExportIcon
              exportRows={rows}
              exportColumns={columns}
              exportTableRef={gridRef}
              reportName={`AutoReleaseHistory_${new Date()
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                })
                .replace(/\//g, "-")}`}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={{ height: 36 }}
            >
              Clear Filters
            </Button>
          </Grid>

          {/* ── Top-level quick filters ── */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{ color: "text.secondary", mb: 0.5, fontWeight: 600 }}
            >
              Quick Filters
            </Typography>
          </Grid>

          {/* SO ID filter */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={distinctSoIds}
              disableCloseOnSelect
              value={filters.so_id}
              onChange={(_, newValue) =>
                setFilters((prev) => ({ ...prev, so_id: newValue }))
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="SO ID"
                  placeholder="Filter by SO ID"
                />
              )}
            />
          </Grid>

          {/* Proposed By filter */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={distinctProposedBy}
              disableCloseOnSelect
              value={filters.proposed_by}
              onChange={(_, newValue) =>
                setFilters((prev) => ({ ...prev, proposed_by: newValue }))
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proposed By"
                  placeholder="Filter by Proposed By"
                />
              )}
            />
          </Grid>

          {/* Row count badge */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Showing{" "}
              <strong>{rows.length}</strong>{" "}
              {rows.length === 1 ? "record" : "records"}
              {rows.length !== allRows.length && ` (filtered from ${allRows.length})`}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Data grid ── */}
      <Paper sx={{ height: 520, p: 1 }}>
        <StyledDataGrid
          ref={gridRef}
          rows={rows}
          columns={columns}
          pageSize={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={DEFAULT_PAGE_SIZE_OPTIONS_MUI}
          disableSelectionOnClick
          components={{ Toolbar: CustomToolbar }}
          sx={{ border: `1px solid ${darkGrey}` }}
        />
      </Paper>
    </Box>
  );
}
